import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Copy, ShieldCheck } from 'lucide-react';
import { useSessao } from '../sessao';
import { CampoTexto } from '../ui/Campos';
import { Botao } from '../ui/Sobreposicoes';
import { confirmarMfa, estadoMfa, inscreverMfa, type EstadoMfa, type InscricaoMfa } from './api';
import { agruparSegredo, bloqueioMfa, bloqueioVigente, erroMfa } from './estado';
import { MfaBloqueado, MfaCarregando, MfaFormularioCodigo, MfaMensagem, useRelogioMfa } from './componentes';

/** A mudança de pessoa ou workspace descarta senha, segredo e código antes de renderizar o novo escopo. */
export function MfaConta() {
  const eu = useSessao();
  return <MfaContaEscopo key={`${eu.usuario.id}/${eu.tenant_id}/${eu.workspace_id}`} />;
}

/** O segredo vive só nesta inscrição em memória, nunca no cache de consultas ou no storage. */
export function MfaChaveInscricao({ inscricao }: { inscricao: InscricaoMfa }) {
  const { t } = useTranslation('mfa');
  const [copia, setCopia] = useState<'nenhuma' | 'feita' | 'erro'>('nenhuma');
  async function copiar() {
    try { await navigator.clipboard.writeText(inscricao.otpauthUri); setCopia('feita'); }
    catch { setCopia('erro'); }
  }
  return <div className="space-y-4">
    <h2 className="text-base font-semibold">{t('passo1')}</h2>
    <p className="text-sm leading-relaxed text-tinta-2">{t('segredoAjuda')}</p>
    <div className="rounded-xl border border-borda bg-sup-2 p-4">
      <p className="mb-2 text-xs text-tinta-3">{t('segredo')}</p>
      <p className="break-words font-mono text-base leading-loose text-tinta-1" aria-label={t('segredo')}>{agruparSegredo(inscricao.segredo)}</p>
    </div>
    <div className="flex min-w-0 flex-col gap-2 text-sm text-tinta-2">
      <label htmlFor="mfa-otpauth-uri">{t('uri')}</label>
      <textarea id="mfa-otpauth-uri" readOnly value={inscricao.otpauthUri} rows={3} spellCheck={false} className="w-full resize-none rounded-lg border border-borda bg-sup-2 p-3 font-mono text-xs text-tinta-1 outline-none focus:border-acento" />
    </div>
    <Botao variante="secundario" onClick={() => void copiar()} className="w-full sm:w-auto"><Copy size={16} />{t(copia === 'feita' ? 'copiado' : 'copiar')}</Botao>
    {copia !== 'nenhuma' && <p role={copia === 'erro' ? 'alert' : 'status'} className={`text-xs ${copia === 'erro' ? 'text-perigo' : 'text-tinta-2'}`}>{t(copia === 'erro' ? 'copiarFalhou' : 'copiado')}</p>}
    <p className="text-xs leading-relaxed text-tinta-3">{t('guardar')}</p>
  </div>;
}

function MfaContaEscopo() {
  const { t } = useTranslation('mfa');
  const [estado, setEstado] = useState<EstadoMfa | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [senha, setSenha] = useState('');
  const [inscricao, setInscricao] = useState<InscricaoMfa | null>(null);
  const [codigo, setCodigo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [bloqueadoAte, setBloqueadoAte] = useState<string | null>(null);
  const vivo = useRef(true);
  const agora = useRelogioMfa();
  const bloqueado = bloqueioVigente(bloqueadoAte, agora);
  const expirado = !!inscricao && Date.parse(inscricao.expiraEm) <= agora;
  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);
  async function carregar(signal?: AbortSignal) {
    setCarregando(true); setErro(null);
    try {
      const atual = await estadoMfa(signal);
      if (vivo.current && !signal?.aborted) { setEstado(atual); setBloqueadoAte(atual.bloqueadoAte); }
    } catch (e) { if (vivo.current && !signal?.aborted) setErro(erroMfa(e)); }
    finally { if (vivo.current && !signal?.aborted) setCarregando(false); }
  }
  useEffect(() => { const abortar = new AbortController(); void carregar(abortar.signal); return () => abortar.abort(); }, []);
  async function inscrever(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (ocupado || bloqueado || !senha) return;
    setOcupado(true); setErro(null);
    const senhaAtual = senha; setSenha('');
    try {
      const nova = await inscreverMfa(senhaAtual);
      if (vivo.current) { setInscricao(nova); setCodigo(''); }
    } catch (e) { if (vivo.current) { setErro(erroMfa(e)); setBloqueadoAte(bloqueioMfa(e)); } }
    finally { if (vivo.current) setOcupado(false); }
  }
  async function confirmar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (!inscricao || ocupado || bloqueado || expirado || codigo.length !== 6) return;
    setOcupado(true); setErro(null);
    const valor = codigo; setCodigo('');
    try {
      await confirmarMfa(valor, inscricao.desafioId);
      if (vivo.current) { setInscricao(null); setSucesso(true); }
    } catch (e) { if (vivo.current) { setErro(erroMfa(e)); setBloqueadoAte(bloqueioMfa(e)); } }
    finally { if (vivo.current) setOcupado(false); }
  }
  return <section className="mx-auto w-full max-w-3xl p-4 sm:p-8" aria-labelledby="mfa-conta-titulo">
    <header className="mb-8 space-y-3">
      <p className="kicker">{t('conta')}</p>
      <h1 id="mfa-conta-titulo" className="flex items-start gap-3 text-2xl font-semibold sm:text-3xl"><ShieldCheck className="mt-1 shrink-0 text-acento-texto" size={26} />{t('titulo')}</h1>
      <p className="max-w-xl text-sm leading-relaxed text-tinta-2">{t('descricao')}</p>
    </header>
    <div className="max-w-xl space-y-6 rounded-2xl border border-borda bg-sup-1 p-5 sm:p-6">
      {carregando ? <MfaCarregando conta /> : sucesso ? <>
        <MfaMensagem tipo="sucesso"><strong className="block">{t('sucessoInscricao')}</strong><p className="mt-1">{t('sucessoInscricaoTexto')}</p></MfaMensagem>
        <Link to="/sala360/resumo" className="inline-flex min-h-10 w-full items-center justify-center rounded-lg bg-acento px-4 text-sm font-medium text-acento-tinta hover:bg-acento-forte focus-visible:outline focus-visible:outline-2 focus-visible:outline-acento sm:w-auto">{t('continuar')}</Link>
      </> : !estado ? <><MfaMensagem tipo="erro">{t(erro ?? 'erro')}</MfaMensagem><Botao variante="secundario" onClick={() => void carregar()}>{t('tentar')}</Botao></> : estado.estado === 'ativo' ? <>
        <MfaMensagem tipo="sucesso"><strong className="block">{t('ativo')}</strong><p className="mt-1">{t('ativoTexto')}</p></MfaMensagem>
        <p className="text-sm text-tinta-2">{t(estado.mfaVerificadoEm ? 'sessaoVerificada' : 'sessaoPendente')}</p>
        {bloqueado && <MfaBloqueado ate={bloqueadoAte} />}
      </> : <>
        {bloqueado ? <MfaBloqueado ate={bloqueadoAte} /> : erro && <MfaMensagem tipo="erro">{t(erro)}</MfaMensagem>}
        {inscricao && !expirado ? <>
          <MfaChaveInscricao inscricao={inscricao} />
          <div className="border-t border-borda pt-5"><h2 className="mb-4 text-base font-semibold">{t('passo2')}</h2><MfaFormularioCodigo codigo={codigo} aoMudar={setCodigo} aoEnviar={confirmar} ocupado={ocupado} desabilitado={bloqueado} rotulo="confirmarInscricao" /></div>
          <Botao variante="fantasma" disabled={ocupado} onClick={() => { setInscricao(null); setCodigo(''); setErro(null); }}>{t('reiniciar')}</Botao>
        </> : <>
          <h2 className="text-base font-semibold">{t(estado.estado === 'inscricao_pendente' ? 'pendente' : 'semMfa')}</h2>
          {estado.estado === 'inscricao_pendente' && <p className="text-sm leading-relaxed text-tinta-2">{t('pendenteTexto')}</p>}
          {expirado && <MfaMensagem tipo="aviso">{t('expirado')}</MfaMensagem>}
          <form onSubmit={inscrever} className="space-y-4" aria-busy={ocupado}>
            <CampoTexto rotulo={t('senha')} ajuda={t('senhaAjuda')} name="senhaAtual" type="password" autoComplete="current-password" required value={senha} onChange={(e) => setSenha(e.target.value)} disabled={ocupado || bloqueado} />
            <Botao type="submit" disabled={ocupado || bloqueado || !senha} className="w-full sm:w-auto">{t(ocupado ? 'iniciando' : estado.estado === 'inscricao_pendente' ? 'retomar' : 'iniciar')}</Botao>
          </form>
        </>}
      </>}
    </div>
  </section>;
}
